import { useEffect, useRef } from 'react';
import { Check, Clock, UserPlus, X } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import type { AdmitPrompt } from '../../lib/meetAdmitQueue';
import type { MeetParticipant } from '../../lib/meetApi';
import { Avatar } from '../tasks/linear/Avatar';

/**
 * "X wants to join" — over the whole meeting, so a knock is never missed in
 * the side panel (hooks/useMeet.ts useAdmitPrompt decides who and when).
 * Acceptă is the default button: Enter accepts, Esc is "Mai târziu", which
 * leaves the request in the waiting list.
 */
export function AdmitRequestOverlay({
  prompt,
  meetingTitle,
  onAccept,
  onDeny,
  onLater,
  onAcceptAll,
}: {
  prompt: AdmitPrompt<MeetParticipant>;
  meetingTitle: string;
  onAccept: (participantId: string) => void;
  onDeny: (participantId: string) => void;
  onLater: (participantId: string) => void;
  onAcceptAll: () => void;
}) {
  const { t } = useI18n();
  const acceptRef = useRef<HTMLButtonElement>(null);
  const person = prompt.current;

  // A new person on the prompt takes the focus back to Acceptă.
  useEffect(() => {
    acceptRef.current?.focus();
  }, [person.id]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onLater(person.id);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [person.id, onLater]);

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="admit-title"
      aria-describedby="admit-subtitle"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
    >
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 text-center shadow-2xl sm:p-8">
        <div className="relative mx-auto h-20 w-20">
          <span className="absolute inset-0 animate-ping rounded-full bg-emerald-500/30" />
          <div className="relative flex h-20 w-20 items-center justify-center rounded-full ring-4 ring-emerald-500/40">
            <Avatar name={person.displayName} size={72} />
          </div>
        </div>

        <h2 id="admit-title" className="mt-5 text-xl font-semibold text-foreground sm:text-2xl">
          {t('meet.admitPopup.title', { name: person.displayName })}
        </h2>
        <p id="admit-subtitle" className="mt-1 text-sm text-muted-foreground">
          {t('meet.admitPopup.subtitle', { title: meetingTitle })}
        </p>
        {person.guest && (
          <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 px-2.5 py-1 text-xs font-medium text-amber-400">
            <UserPlus className="h-3.5 w-3.5" />
            {t('meet.admitPopup.guest')}
          </span>
        )}

        <div className="mt-6 flex flex-col gap-2">
          <button
            ref={acceptRef}
            type="button"
            onClick={() => onAccept(person.id)}
            // `text-white` is the theme's surface colour (dark in dark mode);
            // the label must stay white on the green in both themes.
            style={{ color: '#ffffff' }}
            className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 text-base font-semibold shadow-lg shadow-emerald-900/30 hover:bg-emerald-500 focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-400/50"
          >
            <Check className="h-5 w-5" />
            {t('meet.admitPopup.accept')}
          </button>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => onDeny(person.id)}
              className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl border border-border px-3 text-sm font-medium text-foreground hover:bg-red-500/10 hover:text-red-400"
            >
              <X className="h-4 w-4" />
              {t('meet.admitPopup.deny')}
            </button>
            <button
              type="button"
              onClick={() => onLater(person.id)}
              className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl border border-border px-3 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <Clock className="h-4 w-4" />
              {t('meet.admitPopup.later')}
            </button>
          </div>
        </div>

        {prompt.others > 0 && (
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3 border-t border-border pt-4 text-sm">
            <span className="text-muted-foreground">
              {t('meet.admitPopup.more', { count: prompt.others })}
            </span>
            <button
              type="button"
              onClick={onAcceptAll}
              className="font-medium text-emerald-400 underline-offset-2 hover:underline"
            >
              {t('meet.admitPopup.acceptAll', { count: prompt.total })}
            </button>
          </div>
        )}

        <p className="mt-4 hidden text-xs text-muted-foreground/70 sm:block">
          {t('meet.admitPopup.keys')}
        </p>
      </div>
    </div>
  );
}
